import * as React from "react"
import { AnchorLink } from "gatsby-plugin-anchor-links";
import * as classNames from "classnames";
import useScrollPosition from "../hooks/useScrollPosition";
import HeaderData from "../content/header.json";
import Logo from './logo';
import Button from './button';

const HomeLink = () => {
  return (
    <AnchorLink className="mr-6 unstyled-link" to="/">
      <Logo width={120} inverse={true} className="group-[.floating]:hidden" />
      <Logo width={120} className="group-[.stuck]:hidden" />
    </AnchorLink>
  );
};

const Item = (i, idx) => {
  const wrapper = (child) => <div className="mx-2 my-2 inline-block">{child}</div>
  if (i.children) {
    return wrapper(
      <div className="hs-dropdown [--strategy:static] sm:[--strategy:fixed] [--adaptive:none]">
        <button id={`hs-dropdown-${idx}`} type="button" className="hs-dropdown-toggle flex items-center w-full text-lg">
          {i.text}
          <svg className="ms-1 flex-shrink-0 w-4 h-4 hs-dropdown-open:rotate-180" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </button>

        <div className="hs-dropdown-menu transition-[opacity,margin] duration-[0.1ms] sm:duration-[150ms] hs-dropdown-open:opacity-100 opacity-0 w-48 z-10 bg-transparent before:absolute before:-top-5 before:start-0 before:w-full before:h-5 hidden" aria-labelledby={`hs-dropdown-${idx}`}>
          <div className="flex flex-col md:mt-5 bg-white md:shadow-xl rounded-lg p-2 text-black">
            {i.children.map(Item)}
          </div>
        </div>
      </div>
    );
  }
  if (i.text) {
    return wrapper(<AnchorLink key={idx} className="text-lg" to={i.url}>{i.text}</AnchorLink>);
  }
  if (i.button) {
    return wrapper(
      <AnchorLink key={idx} to={i.url} className="unstyled-link">
        <Button primary={i.primary} className="text-lg">{i.button} </Button>
      </AnchorLink>
    );
  }
  throw new Error("unknown menu item - " + JSON.stringify(i));
}

const LeftLinks = () => (
  <div className="flex-grow">
    <div className="hidden md:inline">
      {HeaderData.left.map(Item)}
    </div>
  </div>
);

const RightLinks = () => (
  <div>
    <div className="hidden md:inline">
      {HeaderData.right.map(Item)}
    </div>
    <div className="inline md:hidden">
      {HeaderData.right.filter(i => i.button).map(Item)}
    </div>
  </div>
);

const MobileLinks = () => (
  <div id="navbar-with-collapse" className="hidden md:hidden basis-full grow px-8">
    <div className={classNames(
      "flex flex-col",
      "mt-5 p-2",
      "bg-white rounded-xl",
      "group-[.stuck]:shadow-xl")
    }>
      {[...HeaderData.left, ...HeaderData.right].filter(i => !i.button).map(Item)}
    </div>
  </div>
);

const MobileCollapse = () => (
  <div className="inline md:hidden text-right ml-2 mr-4 text-black">
    <button type="button" className="hs-collapse-toggle p-2 inline-flex justify-center items-center gap-2 rounded-md border font-medium bg-white shadow-sm align-middle transition-all text-sm" data-hs-collapse="#navbar-with-collapse" aria-controls="navbar-with-collapse" aria-label="Toggle navigation">
      <svg className="hs-collapse-open:hidden w-4 h-4" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fill-rule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z" />
      </svg>
      <svg className="hs-collapse-open:block hidden w-4 h-4" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
      </svg>
    </button>
  </div>
);

const Header = ({ siteTitle }) => {
  const scrollPosition = useScrollPosition();
  return (
    <div id="main-nav" className={classNames(
      "flex justify-center",
      "fixed top-0 md:top-4",
      "bg-transparent", "w-screen", "z-50",
      "group",
      scrollPosition > 0 ? 'floating' : 'stuck'
    )}
    >
      <header className={classNames(
        "rounded-none md:rounded-2xl",
        "flex flex-wrap sm:justify-start sm:flex-nowrap",
        "z-50",
        "w-full md:w-11/12 lg:w-5/6",
        "text-base font-medium",
        "py-2",
        "transition-all",
        "group-[.floating]:bg-white group-[.stuck]:bg-transparent",
        "group-[.floating]:text-black group-[.stuck]:md:text-primary-text",
        "group-[.floating]:shadow-xl"
      )}>
        <nav aria-label="Global" className="flex flex-col w-full">
          <div className={classNames(
            "flex w-full",
            "px-4 lg:px-8",
            "items-center")}>
            <HomeLink />
            <LeftLinks />
            <RightLinks />
            <MobileCollapse />
          </div>
          <MobileLinks />
        </nav>
      </header>
    </div>
  );
};

export default Header
